import {Component, Input} from '@angular/core';

/**
 * navItem "Beträge" of the Provisionssatz OBC, widget alias `cp.contractAgent.amounts`.
 *
 * The money side of the row: the calculated `provisionAmount` next to its manual overwrite
 * (`provisionAmountOverwrite` with `overwriteSource` and the mandatory `overwriteReason`), the
 * split into Rücklage and Auszahlung (`reserveTargetAmount` / `provisionTargetAmount`), the
 * `totalAmount` the liability is carried on, and the three applied matrix parameters.
 *
 * SCALES: amounts are EUR at AMOUNT_SCALE and percentages at PERCENTAGE_SCALE. Both are decided
 * by `ProvisionModuleConfig` on the backend and reach the UI through the entity metadata — this
 * component deliberately does NOT re-format them, which would fork the rounding.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * SIX DEFERRED FIELDS — the largest block of the Satz-level honesty duty (HIGH-1)
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * Six of the ten fields the run owns outright are rendered here. The test that puts a field in
 * that set is the one on `CpProvisionContractWizardStateService.DEFERRED_CONTRACT_AGENT_FIELDS`:
 * nullable without a default AND written by `ProvisionCalculationService` alone. Verified on
 * `ProvisionContractAgent.java` — none of the six carries `@NotNull` or `nullable = false`:
 *
 *   provisionAmount          :536  @Column(precision = 19, scale = 2)
 *   reserveTargetAmount      :623  @Column(precision = 19, scale = 2)
 *   provisionTargetAmount    :646  @Column(precision = 19, scale = 2)
 *   appliedFactor            :491  @Column(precision = 10, scale = 4)
 *   appliedCalcMode          :470  @Column
 *   appliedReservePercentage :513  @Column(precision = 5, scale = 2)
 *
 * `provisionAmount` is the one that matters most on the whole OBC: it is the figure a caseworker
 * opens a Provisionssatz FOR. Rendered empty it reads as "dieser Satz ist nichts wert", which is a
 * statement about the money — the opposite of the one the data supports.
 *
 * WHY `appliedReservePercentage` IS NOT A DASH FIELD (corrected under HIGH-1): it was classified as
 * a share whose unknown value renders "—" (business rule 2). That was the wrong category. The rule
 * exists so an unknown quota is never printed as 0 % or 100 %; it says nothing about a value the
 * run has simply not produced yet. An em dash in a deferred field is in fact the exact cell the
 * second half of the evidence assertion forbids — "no blank/— cell in its place". The field is a
 * deferred one, so it belongs in {@link pendingFields}, and this pane now declares NO dash fields
 * at all.
 *
 * NOT deferred, deliberately: `provisionAmountOverwrite`, `overwriteSource` and `overwriteReason`
 * are written by a caseworker through the Betrags-Overwrite dialog, never by the run — an empty
 * overwrite is a lasting statement ("nicht überschrieben") and must not be dressed up as pending.
 * `totalAmount` (:669) is `@NotNull @Column(nullable = false)` and can never stand empty.
 */
@Component({
    selector: 'cp-provision-contract-agent-amounts',
    templateUrl: './cp-provision-contract-agent-amounts.component.html',
    styleUrls: ['./cp-provision-contract-agent-amounts.component.scss'],
    standalone: false
})
export class CpProvisionContractAgentAmountsComponent {

    @Input() provisionContractAgentId: number;

    /** Fields per row — the side view passes 1. */
    @Input() columns: number = 2;

    /** Visible fields, in the order of the requirement. */
    readonly fields: string[] = [
        'provisionAmount',
        'provisionAmountOverwrite',
        'overwriteSource',
        'overwriteReason',
        'reserveTargetAmount',
        'provisionTargetAmount',
        'totalAmount',
        'appliedFactor',
        'appliedCalcMode',
        'appliedReservePercentage'
    ];

    /**
     * The six deferred fields rendered on this pane — see the verification table in the class
     * javadoc. Each carries "nicht berechnet" while it is empty, with the block-level
     * explanation once above them.
     */
    readonly pendingFields: string[] = [
        'provisionAmount',
        'reserveTargetAmount',
        'provisionTargetAmount',
        'appliedFactor',
        'appliedCalcMode',
        'appliedReservePercentage'
    ];

}
